import React from 'react';
import { Layout, Table, Spin, Modal } from 'antd';
import { Footer } from '@src/components/common';
import { useRuleSummary, useDeleteRule, useToggleRule } from './utils/hooks';
import { AlertForwardRule } from './utils/types';
import { RuleHeader, OutlineBlueButton, DetailContentDiv, TitleLayout } from './utils/styled';
import { useNavigate } from 'react-router-dom';
import { getAlertForwardColumns } from './utils/helper';

const { Content } = Layout;

const AlertRuleListPage = () => {
  const [mutatingId, setMutatingId] = React.useState<number | null>(null);
  const { confirm } = Modal;

  const {
    isLoading: ruleListLoading,
    isFetching: ruleListFetching,
    data: ruleListData,
  } = useRuleSummary();

  React.useEffect(() => {
    if (!ruleListFetching) {
      setMutatingId(null);
    }
  }, [ruleListFetching]);

  const navigate = useNavigate();
  const deleteRuleMutation = useDeleteRule();
  const toggleRuleMutation = useToggleRule();

  const handleMenuClick = (key: string, record: AlertForwardRule) => {
    switch (key) {
      case 'edit':
        navigate(`/app/settings/alert-forward-edit`, {
          state: { rule: record },
        });
        break;

      case 'duplicate': {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = record; // Exclude id from the cloned object
        const clone: Omit<AlertForwardRule, 'id'> = {
          ...rest,
          name: `${record.name} - Copy`,
        };
        navigate(`/app/settings/alert-forward-edit`, {
          state: { rule: clone },
        });
        break;
      }
      case 'disable':
      case 'enable':
        setMutatingId(record.id);
        toggleRuleMutation.mutate(record);
        break;

      case 'delete':
        confirm({
          title: 'Are you sure you want to delete this rule?',
          content: `Rule: ${record.name}`,
          okText: 'Yes, Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: () => {
            setMutatingId(record.id);
            deleteRuleMutation.mutate(record.id, {
              onSettled: () => setMutatingId(null),
            });
          },
        });
        break;

      default:
        break;
    }
  };

  const columns = getAlertForwardColumns({
    mutatingId,
    isMutating: toggleRuleMutation.isLoading || ruleListFetching,
    handleMenuClick,
  });

  return (
    <Layout className="rule-page">
      <Content>
        <TitleLayout level={4}>Notification Channels / AI Alert Forwarding Rules</TitleLayout>

        <DetailContentDiv>
          <RuleHeader>
            <OutlineBlueButton
              loading={ruleListLoading || ruleListFetching}
              onClick={() => {
                navigate(`/app/settings/alert-forward-edit`, {
                  state: { rule: undefined },
                });
              }}
            >
              Add New Rule
            </OutlineBlueButton>
          </RuleHeader>

          <Spin spinning={ruleListLoading || ruleListFetching}>
            <Table<AlertForwardRule>
              className="rule-table"
              columns={columns}
              dataSource={ruleListData}
              rowKey="key"
              size="small"
              pagination={false}
            />
          </Spin>
        </DetailContentDiv>
      </Content>
      <Footer />
    </Layout>
  );
};

export default AlertRuleListPage;
